import type { Task, TaskComment, TaskAttachment, TaskActivity, TaskAssignee, TaskStatistics } from '@/types/task';

// Demo Users/Assignees
export const demoAssignees: TaskAssignee[] = [
  { id: 'user-1', name: 'Sarah Johnson', email: 'sarah.j@company.com', type: 'user', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100' },
  { id: 'user-2', name: 'Michael Chen', email: 'michael.c@company.com', type: 'user', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100' },
  { id: 'user-3', name: 'Emily Rodriguez', email: 'emily.r@company.com', type: 'user', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100' },
  { id: 'user-4', name: 'David Martinez', email: 'david.m@company.com', type: 'user', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100' },
  { id: 'user-5', name: 'Jennifer Lee', email: 'jennifer.l@company.com', type: 'user', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100' },
  { id: 'user-6', name: 'Robert Thompson', email: 'robert.t@company.com', type: 'user', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100' },
  { id: 'group-1', name: 'IT Support Team', email: 'it-support@company.com', type: 'group' },
  { id: 'group-2', name: 'Audit Team', email: 'audit@company.com', type: 'group' },
  { id: 'group-3', name: 'Finance Team', email: 'finance@company.com', type: 'group' },
];

// Demo Tasks
export const demoTasks: Task[] = [
  // Overdue Critical Tasks
  {
    id: 'TASK-001',
    title: 'Critical: Database Sync Failure - Production Assets',
    description: 'Asset synchronization between mobile app and backend database is failing intermittently. Approximately 15% of asset updates from field audits are not being saved. This is causing data inconsistencies and audit discrepancies.\n\nSteps to reproduce:\n1. Complete asset audit on mobile app\n2. Submit audit results\n3. Check backend database\n4. Notice missing records\n\nImpact: High - affecting audit accuracy and compliance reporting.',
    type: 'Maintenance Request',
    priority: 'Urgent',
    status: 'In Progress',
    assignee: demoAssignees[3], // David Martinez
    assignedBy: 'Sarah Johnson',
    assignedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days overdue
    startDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    links: [
      { id: 'link-1', type: 'asset', entityId: 'asset-db-001', entityName: 'Production Database Server' },
      { id: 'link-2', type: 'audit', entityId: 'audit-2024-q4', entityName: 'Q4 2024 Asset Audit' },
    ],
    linkedAssets: [
      { 
        id: 'asset-link-1', 
        assetId: 'asset-db-001', 
        assetCode: 'DB-PROD-001', 
        assetName: 'Production Database Server', 
        assetImageUrl: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400',
        assetStatus: 'active',
        assetCategory: 'Computer Equipment',
        linkedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        linkedBy: 'Sarah Johnson'
      },
      { 
        id: 'asset-link-2', 
        assetId: 'asset-backup-001', 
        assetCode: 'DB-BACKUP-001', 
        assetName: 'Backup Database Server', 
        assetImageUrl: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=400',
        assetStatus: 'active',
        assetCategory: 'Computer Equipment',
        linkedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        linkedBy: 'Sarah Johnson'
      },
    ],
    tags: ['critical', 'production', 'data-integrity'],
    checklists: [
      { id: 'check-1', title: 'Identify affected records', completed: true, createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'David Martinez' },
      { id: 'check-2', title: 'Review sync logs', completed: true, createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'David Martinez' },
      { id: 'check-3', title: 'Implement fix', completed: false, createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-4', title: 'Test in staging', completed: false, createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-5', title: 'Deploy to production', completed: false, createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString() },
    ],
    subtasks: [],
    createdBy: 'Sarah Johnson',
    updatedBy: 'David Martinez',
    commentsCount: 12,
    attachmentsCount: 3,
    lastActivityAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'David Martinez',
  },
  {
    id: 'TASK-002',
    title: 'Resolve Asset Location Discrepancies - Building A',
    description: 'Physical audit revealed 23 assets in Building A with location mismatches. Need to verify actual locations, update system records, and investigate root cause of discrepancies.',
    type: 'General Task',
    priority: 'Urgent',
    status: 'Blocked',
    assignee: demoAssignees[1], // Michael Chen
    assignedBy: 'Jennifer Lee',
    assignedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day overdue
    startDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    links: [
      { id: 'link-3', type: 'location', entityId: 'loc-building-a', entityName: 'Building A' },
      { id: 'link-4', type: 'audit', entityId: 'audit-2024-q4-building-a', entityName: 'Building A Physical Audit' },
      { id: 'link-5', type: 'exception', entityId: 'exc-001', entityName: 'Location Mismatch Exception' },
    ],
    linkedAssets: [
      { 
        id: 'asset-link-3', 
        assetId: 'asset-laptop-042', 
        assetCode: 'LT-042', 
        assetName: 'Dell Latitude 5420 - Marketing', 
        assetImageUrl: 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400',
        assetStatus: 'active',
        assetCategory: 'Computer Equipment',
        linkedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        linkedBy: 'Jennifer Lee'
      },
    ],
    tags: ['audit', 'location', 'urgent'],
    checklists: [
      { id: 'check-6', title: 'Get building access approval', completed: false, createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-7', title: 'Schedule physical verification', completed: false, createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-8', title: 'Update asset records', completed: false, createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString() },
    ],
    subtasks: [],
    createdBy: 'Jennifer Lee',
    updatedBy: 'Michael Chen',
    commentsCount: 8,
    attachmentsCount: 5,
    lastActivityAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'Michael Chen',
  },

  // Due Today - High Priority
  {
    id: 'TASK-003',
    title: 'Complete Q1 2025 Depreciation Schedule Review',
    description: 'Review and update the depreciation schedule for all IT equipment purchased in Q4 2024. Ensure compliance with new accounting standards and verify all asset values are correctly calculated.',
    type: 'General Task',
    priority: 'High',
    status: 'In Progress',
    assignee: demoAssignees[5], // Robert Thompson
    assignedBy: 'Sarah Johnson',
    assignedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date().toISOString(), // Due today
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    links: [
      { id: 'link-6', type: 'asset', entityId: 'asset-category-it', entityName: 'IT Equipment Category' },
    ],
    linkedAssets: [],
    tags: ['finance', 'depreciation', 'compliance'],
    checklists: [
      { id: 'check-9', title: 'Export Q4 2024 asset list', completed: true, createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Robert Thompson' },
      { id: 'check-10', title: 'Review depreciation rates', completed: true, createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Robert Thompson' },
      { id: 'check-11', title: 'Update asset values', completed: false, createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-12', title: 'Generate compliance report', completed: false, createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() },
    ],
    subtasks: [],
    createdBy: 'Sarah Johnson',
    updatedBy: 'Robert Thompson',
    commentsCount: 5,
    attachmentsCount: 2,
    lastActivityAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'Robert Thompson',
  },
  {
    id: 'TASK-004',
    title: 'Implement Barcode Scanning for Asset Check-In',
    description: 'Add barcode/QR code scanning capability to the mobile app for faster asset check-in during audits. This feature will significantly reduce manual data entry time and improve accuracy. Should support both 1D barcodes and QR codes.',
    type: 'Inspection',
    priority: 'High',
    status: 'In Progress',
    assignee: demoAssignees[2], // Emily Rodriguez
    assignedBy: 'David Martinez',
    assignedAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    dueDate: new Date().toISOString(), // Due today
    startDate: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    links: [],
    linkedAssets: [],
    tags: ['mobile', 'feature', 'enhancement'],
    checklists: [
      { id: 'check-13', title: 'Research barcode libraries', completed: true, createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Emily Rodriguez' },
      { id: 'check-14', title: 'Design UI mockups', completed: true, createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Emily Rodriguez' },
      { id: 'check-15', title: 'Implement scanner component', completed: true, createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Emily Rodriguez' },
      { id: 'check-16', title: 'Test on iOS devices', completed: false, createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-17', title: 'Test on Android devices', completed: false, createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString() },
    ],
    subtasks: [
      { id: 'sub-1', title: 'Add camera permissions', status: 'Resolved', assigneeId: 'user-3', assigneeName: 'Emily Rodriguez' },
      { id: 'sub-2', title: 'Integrate barcode library', status: 'Resolved', assigneeId: 'user-3', assigneeName: 'Emily Rodriguez' },
      { id: 'sub-3', title: 'Create scanner UI', status: 'In Progress', assigneeId: 'user-3', assigneeName: 'Emily Rodriguez' },
    ],
    createdBy: 'David Martinez',
    updatedBy: 'Emily Rodriguez',
    commentsCount: 18,
    attachmentsCount: 7,
    lastActivityAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    lastActivityBy: 'Emily Rodriguez',
  },

  // Due This Week - Medium Priority
  {
    id: 'TASK-005',
    title: 'Database Performance Optimization',
    description: 'Optimise database queries for asset search and reporting functions. Current response times are exceeding acceptable thresholds during peak usage hours. Need to add indexes, optimise queries, and implement caching strategies.',
    type: 'Documentation Update',
    priority: 'High',
    status: 'In Progress',
    assignee: demoAssignees[3], // David Martinez
    assignedBy: 'Sarah Johnson',
    assignedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days from now
    startDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    links: [
      { id: 'link-7', type: 'asset', entityId: 'asset-db-001', entityName: 'Asset Management Database' },
    ],
    linkedAssets: [],
    tags: ['performance', 'database', 'optimization'],
    checklists: [
      { id: 'check-18', title: 'Analyse slow queries', completed: true, createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'David Martinez' },
      { id: 'check-19', title: 'Add database indexes', completed: true, createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'David Martinez' },
      { id: 'check-20', title: 'Implement query caching', completed: false, createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-21', title: 'Performance testing', completed: false, createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString() },
    ],
    subtasks: [],
    createdBy: 'Sarah Johnson',
    updatedBy: 'David Martinez',
    commentsCount: 9,
    attachmentsCount: 4,
    lastActivityAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'David Martinez',
  },
  {
    id: 'TASK-006',
    title: 'Export to Excel Missing Asset Categories',
    description: 'When exporting asset reports to Excel, some asset categories are not being included in the export. Specifically, "Network Equipment" and "Furniture" categories are missing from the exported file.',
    type: 'Maintenance Request',
    priority: 'Medium',
    status: 'New',
    assignee: demoAssignees[2], // Emily Rodriguez
    assignedBy: 'Robert Thompson',
    assignedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days from now
    links: [],
    linkedAssets: [],
    tags: ['export', 'bug', 'reporting'],
    checklists: [],
    subtasks: [],
    createdBy: 'Robert Thompson',
    updatedBy: 'Robert Thompson',
    commentsCount: 3,
    attachmentsCount: 2,
    lastActivityAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'Robert Thompson',
  },
  {
    id: 'TASK-007',
    title: 'Update Security Patches for Asset Management System',
    description: 'Apply latest security patches and updates to the asset management system. Includes backend API updates, database security enhancements, and frontend dependency updates. Scheduled maintenance window required.',
    type: 'Documentation Update',
    priority: 'Medium',
    status: 'In Progress',
    assignee: demoAssignees[3], // David Martinez
    assignedBy: 'Sarah Johnson',
    assignedAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString(), // 4 days from now
    startDate: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    links: [],
    linkedAssets: [],
    tags: ['security', 'maintenance', 'updates'],
    checklists: [
      { id: 'check-22', title: 'Review security advisories', completed: true, createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'David Martinez' },
      { id: 'check-23', title: 'Update backend dependencies', completed: false, createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-24', title: 'Update frontend dependencies', completed: false, createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-25', title: 'Schedule maintenance window', completed: false, createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString() },
    ],
    subtasks: [],
    createdBy: 'Sarah Johnson',
    updatedBy: 'David Martinez',
    commentsCount: 6,
    attachmentsCount: 1,
    lastActivityAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'David Martinez',
  },

  // Upcoming - Lower Priority
  {
    id: 'TASK-008',
    title: 'Add Dark Mode Support to Dashboard',
    description: 'Implement dark mode theme option for the asset management dashboard. This will improve user experience for users who prefer dark themes and reduce eye strain during extended use.',
    type: 'Inspection',
    priority: 'Low',
    status: 'New',
    assignee: demoAssignees[2], // Emily Rodriguez
    assignedBy: 'Sarah Johnson',
    assignedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(), // 21 days from now
    links: [],
    linkedAssets: [],
    tags: ['ui', 'enhancement', 'accessibility'],
    checklists: [],
    subtasks: [],
    createdBy: 'Sarah Johnson',
    updatedBy: 'Sarah Johnson',
    commentsCount: 2,
    attachmentsCount: 0,
    lastActivityAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'Sarah Johnson',
  },
  {
    id: 'TASK-009',
    title: 'Create User Training Materials for New Features',
    description: 'Develop comprehensive training materials for the new barcode scanning and mobile audit features. Include video tutorials, step-by-step guides, and FAQ documentation.',
    type: 'General Task',
    priority: 'Medium',
    status: 'New',
    assignee: demoAssignees[4], // Jennifer Lee
    assignedBy: 'Sarah Johnson',
    assignedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days from now
    links: [],
    linkedAssets: [],
    tags: ['documentation', 'training', 'user-support'],
    checklists: [
      { id: 'check-26', title: 'Outline training topics', completed: false, createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-27', title: 'Record video tutorials', completed: false, createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-28', title: 'Write step-by-step guides', completed: false, createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-29', title: 'Create FAQ document', completed: false, createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString() },
    ],
    subtasks: [],
    createdBy: 'Sarah Johnson',
    updatedBy: 'Sarah Johnson',
    commentsCount: 1,
    attachmentsCount: 0,
    lastActivityAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'Sarah Johnson',
  },
  {
    id: 'TASK-010',
    title: 'Implement Asset Transfer Workflow',
    description: 'Create a new workflow for transferring assets between locations and departments. Include approval process, notification system, and audit trail.',
    type: 'Inspection',
    priority: 'Medium',
    status: 'New',
    assignee: demoAssignees[3], // David Martinez
    assignedBy: 'Sarah Johnson',
    assignedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(), // 28 days from now
    links: [],
    linkedAssets: [],
    tags: ['workflow', 'feature', 'asset-management'],
    checklists: [],
    subtasks: [],
    createdBy: 'Sarah Johnson',
    updatedBy: 'Sarah Johnson',
    commentsCount: 0,
    attachmentsCount: 0,
    lastActivityAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'Sarah Johnson',
  },

  // Recently Completed
  {
    id: 'TASK-011',
    title: 'Conduct Q4 2024 Asset Audit - IT Department',
    description: 'Complete comprehensive physical audit of all IT assets in the IT Department. Verify asset locations, conditions, and custodian assignments. Update system records with any changes discovered during the audit.',
    type: 'General Task',
    priority: 'High',
    status: 'Resolved',
    assignee: demoAssignees[4], // Jennifer Lee
    assignedBy: 'Michael Chen',
    assignedAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    startDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    resolvedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    links: [
      { id: 'link-8', type: 'audit', entityId: 'audit-2024-q4-it', entityName: 'Q4 2024 IT Department Audit' },
      { id: 'link-9', type: 'location', entityId: 'loc-it-dept', entityName: 'IT Department' },
    ],
    linkedAssets: [],
    tags: ['audit', 'completed', 'it-department'],
    checklists: [
      { id: 'check-30', title: 'Prepare audit checklist', completed: true, createdAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Jennifer Lee' },
      { id: 'check-31', title: 'Conduct physical verification', completed: true, createdAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Jennifer Lee' },
      { id: 'check-32', title: 'Update asset records', completed: true, createdAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Jennifer Lee' },
      { id: 'check-33', title: 'Generate audit report', completed: true, createdAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Jennifer Lee' },
    ],
    subtasks: [],
    createdBy: 'Michael Chen',
    updatedBy: 'Jennifer Lee',
    commentsCount: 22,
    attachmentsCount: 12,
    lastActivityAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'Jennifer Lee',
  },
  {
    id: 'TASK-012',
    title: 'Fix Mobile App Crash on Asset Photo Upload',
    description: 'Mobile app crashes when uploading photos larger than 5MB. Implement image compression before upload to prevent crashes and improve upload performance.',
    type: 'Maintenance Request',
    priority: 'High',
    status: 'Closed',
    assignee: demoAssignees[2], // Emily Rodriguez
    assignedBy: 'David Martinez',
    assignedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    resolvedAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(),
    closedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    links: [],
    linkedAssets: [],
    tags: ['mobile', 'bug', 'fixed'],
    checklists: [
      { id: 'check-34', title: 'Reproduce issue', completed: true, createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Emily Rodriguez' },
      { id: 'check-35', title: 'Implement image compression', completed: true, createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 22 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Emily Rodriguez' },
      { id: 'check-36', title: 'Test on multiple devices', completed: true, createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 18 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Emily Rodriguez' },
      { id: 'check-37', title: 'Deploy to production', completed: true, createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(), completedBy: 'Emily Rodriguez' },
    ],
    subtasks: [],
    createdBy: 'David Martinez',
    updatedBy: 'Emily Rodriguez',
    commentsCount: 14,
    attachmentsCount: 5,
    lastActivityAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'Emily Rodriguez',
  },

  // Group-assigned tasks
  {
    id: 'TASK-013',
    title: 'Review and Update Asset Disposal Procedures',
    description: 'Review current asset disposal procedures and update documentation to reflect new environmental regulations and data security requirements.',
    type: 'General Task',
    priority: 'Medium',
    status: 'New',
    assignee: demoAssignees[7], // Audit Team (group)
    assignedBy: 'Sarah Johnson',
    assignedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
    links: [],
    linkedAssets: [],
    tags: ['compliance', 'documentation', 'disposal'],
    checklists: [],
    subtasks: [],
    createdBy: 'Sarah Johnson',
    updatedBy: 'Sarah Johnson',
    commentsCount: 0,
    attachmentsCount: 0,
    lastActivityAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'Sarah Johnson',
  },
  {
    id: 'TASK-014',
    title: 'Investigate Network Equipment Connectivity Issues',
    description: 'Several network switches in Building C are reporting intermittent connectivity issues. Investigate root cause and implement fix.',
    type: 'Maintenance Request',
    priority: 'High',
    status: 'New',
    assignee: demoAssignees[6], // IT Support Team (group)
    assignedBy: 'Michael Chen',
    assignedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    links: [
      { id: 'link-10', type: 'location', entityId: 'loc-building-c', entityName: 'Building C' },
      { id: 'link-11', type: 'asset', entityId: 'asset-network-switches', entityName: 'Network Switches' },
    ],
    linkedAssets: [],
    tags: ['network', 'urgent', 'infrastructure'],
    checklists: [],
    subtasks: [],
    createdBy: 'Michael Chen',
    updatedBy: 'Michael Chen',
    commentsCount: 4,
    attachmentsCount: 1,
    lastActivityAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'Michael Chen',
  },
  {
    id: 'TASK-015',
    title: 'Prepare Annual Asset Valuation Report',
    description: 'Compile annual asset valuation report for financial year end. Include depreciation schedules, asset additions/disposals, and current asset values.',
    type: 'General Task',
    priority: 'High',
    status: 'In Progress',
    assignee: demoAssignees[8], // Finance Team (group)
    assignedBy: 'Sarah Johnson',
    assignedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    startDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    links: [],
    linkedAssets: [],
    tags: ['finance', 'reporting', 'annual'],
    checklists: [
      { id: 'check-38', title: 'Gather asset data', completed: true, createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-39', title: 'Calculate depreciation', completed: true, createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), completedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-40', title: 'Compile report', completed: false, createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString() },
      { id: 'check-41', title: 'Review with CFO', completed: false, createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString() },
    ],
    subtasks: [],
    createdBy: 'Sarah Johnson',
    updatedBy: 'Finance Team',
    commentsCount: 7,
    attachmentsCount: 3,
    lastActivityAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    lastActivityBy: 'Finance Team',
  },
];

// Demo Comments
export const demoComments: TaskComment[] = [
  {
    id: 'comment-1',
    taskId: 'TASK-001',
    content: 'I\'ve identified the root cause - there\'s a race condition in the sync service when multiple devices try to update the same asset simultaneously. Working on a fix using optimistic locking.',
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-4',
    createdByName: 'David Martinez',
    createdByAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
  },
  {
    id: 'comment-2',
    taskId: 'TASK-001',
    content: 'Great work! How long do you estimate for the fix and testing?',
    createdAt: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-1',
    createdByName: 'Sarah Johnson',
    createdByAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100',
  },
  {
    id: 'comment-3',
    taskId: 'TASK-002',
    content: 'Still waiting for building access approval from facilities. I\'ve escalated to management.',
    createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-2',
    createdByName: 'Michael Chen',
    createdByAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
  },
  {
    id: 'comment-4',
    taskId: 'TASK-004',
    content: 'Scanner is working great on iOS! Testing on Android devices now. Found a minor UI issue with the camera preview on Samsung devices.',
    createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    createdBy: 'user-3',
    createdByName: 'Emily Rodriguez',
    createdByAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100',
  },
];

// Demo Attachments
export const demoAttachments: TaskAttachment[] = [
  {
    id: 'attach-1',
    taskId: 'TASK-001',
    fileName: 'sync-error-logs.txt',
    fileSize: 45678,
    fileType: 'text/plain',
    uploadedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    uploadedBy: 'user-4',
    uploadedByName: 'David Martinez',
    url: '#',
  },
  {
    id: 'attach-2',
    taskId: 'TASK-001',
    fileName: 'database-analysis.pdf',
    fileSize: 234567,
    fileType: 'application/pdf',
    uploadedAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    uploadedBy: 'user-4',
    uploadedByName: 'David Martinez',
    url: '#',
  },
  {
    id: 'attach-3',
    taskId: 'TASK-002',
    fileName: 'location-discrepancies.xlsx',
    fileSize: 123456,
    fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    uploadedAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    uploadedBy: 'user-2',
    uploadedByName: 'Michael Chen',
    url: '#',
  },
];

// Demo Activity
export const demoActivity: TaskActivity[] = [
  {
    id: 'activity-1',
    taskId: 'TASK-001',
    type: 'status-change',
    description: 'changed status',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-4',
    createdByName: 'David Martinez',
    oldValue: 'New',
    newValue: 'In Progress',
  },
  {
    id: 'activity-2',
    taskId: 'TASK-001',
    type: 'comment',
    description: 'added a comment',
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-4',
    createdByName: 'David Martinez',
  },
  {
    id: 'activity-3',
    taskId: 'TASK-001',
    type: 'checklist-completed',
    description: 'completed checklist item',
    createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-4',
    createdByName: 'David Martinez',
    newValue: 'Identify affected records',
  },
  {
    id: 'activity-4',
    taskId: 'TASK-001',
    type: 'link-added',
    description: 'linked asset',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-1',
    createdByName: 'Sarah Johnson',
    newValue: 'Production Database Server',
  },
  {
    id: 'activity-5',
    taskId: 'TASK-001',
    type: 'assignee-change',
    description: 'assigned task',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-1',
    createdByName: 'Sarah Johnson',
    oldValue: 'Unassigned',
    newValue: 'David Martinez',
  },
  {
    id: 'activity-6',
    taskId: 'TASK-002',
    type: 'status-change',
    description: 'changed status',
    createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-2',
    createdByName: 'Michael Chen',
    oldValue: 'In Progress',
    newValue: 'Blocked',
  },
  {
    id: 'activity-7',
    taskId: 'TASK-002',
    type: 'comment',
    description: 'added a comment',
    createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-2',
    createdByName: 'Michael Chen',
  },
  {
    id: 'activity-8',
    taskId: 'TASK-003',
    type: 'status-change',
    description: 'changed status',
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-6',
    createdByName: 'Robert Thompson',
    oldValue: 'New',
    newValue: 'In Progress',
  },
  {
    id: 'activity-9',
    taskId: 'TASK-003',
    type: 'checklist-completed',
    description: 'completed checklist item',
    createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-6',
    createdByName: 'Robert Thompson',
    newValue: 'Export Q4 2024 asset list',
  },
  {
    id: 'activity-10',
    taskId: 'TASK-004',
    type: 'status-change',
    description: 'changed status',
    createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-3',
    createdByName: 'Emily Rodriguez',
    oldValue: 'New',
    newValue: 'In Progress',
  },
  {
    id: 'activity-11',
    taskId: 'TASK-004',
    type: 'comment',
    description: 'added a comment',
    createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    createdBy: 'user-3',
    createdByName: 'Emily Rodriguez',
  },
  {
    id: 'activity-12',
    taskId: 'TASK-004',
    type: 'tag-added',
    description: 'added tag',
    createdAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-3',
    createdByName: 'Emily Rodriguez',
    newValue: 'mobile',
  },
];

// Helper function to get task statistics
export function getTaskStatistics(tasks: Task[] = demoTasks): TaskStatistics {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekFromNow = new Date(today);
  weekFromNow.setDate(weekFromNow.getDate() + 7);

  return {
    totalTasks: tasks.length,
    byStatus: {
      'New': tasks.filter(t => t.status === 'New').length,
      'In Progress': tasks.filter(t => t.status === 'In Progress').length,
      'Blocked': tasks.filter(t => t.status === 'Blocked').length,
      'Resolved': tasks.filter(t => t.status === 'Resolved').length,
      'Closed': tasks.filter(t => t.status === 'Closed').length,
    },
    byType: {
      'Task': tasks.filter(t => t.type === 'Task').length,
      'Bug': tasks.filter(t => t.type === 'Bug').length,
      'Feature': tasks.filter(t => t.type === 'Feature').length,
      'Maintenance': tasks.filter(t => t.type === 'Maintenance').length,
      'General Task': tasks.filter(t => t.type === 'General Task').length,
      'Inspection': tasks.filter(t => t.type === 'Inspection').length,
      'Maintenance Request': tasks.filter(t => t.type === 'Maintenance Request').length,
      'Documentation Update': tasks.filter(t => t.type === 'Documentation Update').length,
      'Verification': tasks.filter(t => t.type === 'Verification').length,
      'Follow-up Action': tasks.filter(t => t.type === 'Follow-up Action').length,
      'Data Collection': tasks.filter(t => t.type === 'Data Collection').length,
    },
    byPriority: {
      'Urgent': tasks.filter(t => t.priority === 'Urgent').length,
      'High': tasks.filter(t => t.priority === 'High').length,
      'Medium': tasks.filter(t => t.priority === 'Medium').length,
      'Low': tasks.filter(t => t.priority === 'Low').length,
    },
    overdue: tasks.filter(t => t.dueDate && new Date(t.dueDate) < today && t.status !== 'Resolved' && t.status !== 'Closed').length,
    dueToday: tasks.filter(t => {
      if (!t.dueDate || t.status === 'Resolved' || t.status === 'Closed') return false;
      const dueDate = new Date(t.dueDate);
      return dueDate.toDateString() === today.toDateString();
    }).length,
    dueThisWeek: tasks.filter(t => {
      if (!t.dueDate || t.status === 'Resolved' || t.status === 'Closed') return false;
      const dueDate = new Date(t.dueDate);
      return dueDate >= today && dueDate < weekFromNow;
    }).length,
  };
}
